<template>
  <div
    class="vz-individual-order-header flex-align-items-center pdf-order-section-hide"
    :class="{'border-bottom-unset': isLightOrder}"
  >
    <div class="vz-individual-order-header-label">
      <div class="flex flex-align-items-center mt-10">
        <span class="text-h1">
          {{ orderTitle }}
        </span>
      </div>
      <vz-order-status :order="orderStore.orderForm" :multi="isMultiOrder" class="mt-5"/>
    </div>

    <vz-toggler
      v-if="isNew && !isLightOrder"
      :modelValue="isVip"
      :variant="togglerVariant"
      @change="onChangeVip"
    >
      <vz-label
        without-dot
        :tooltip-text="vipText"
        :variant="togglerLabelVariant"
        class="mb-0"
      >
        Индивидуальное <br>обслуживание
      </vz-label>
    </vz-toggler>

    <div class="vz-individual-order-header-controls">
      <vz-button
        v-if="!isNew"
        class="vz-order-header-button"
        outline
        icon-name="arrow-left"
        @click="onViewOrder"
      >
        Вернуться к заказу
      </vz-button>
      <vz-button
        v-if="favors.clone"
        :href="orderCloneUrl"
        outline
        class="vz-order-header-button"
        variant="dark-gray"
        target="_blank"
        title="Создать на основе"
        icon-name="content-copy"
        @click="onCopyClick"
      >
        <span>Создать на основе</span>
      </vz-button>
      <vz-button
        v-else
        outline
        variant="info"
        icon-url="/svg/support-agent.svg"
        icon-color="#2B61B4"
        class="vz-order-header-button"
        @click="showManageHelp"
      >
        Помощь менеджера
      </vz-button>
    </div>

    <div
      v-if="orderStore.orderForm.meta?.status?.message"
      class="vz-info-order-message"
    >
      <vz-icon
        name="information-variant-circle"
        size="20"
      />
      {{ orderStore.orderForm.meta.status.message }}
    </div>

    <vz-order-view-header-block-multi-offer v-if="isPublic">
      <template #button>
        <vz-button
          big
          variant="primary"
          :to="{ name: 'free-group-pickup', query: {'utm_source': 'gruppzabor', 'utm_medium': 'public', 'utm_campaign': 'create'} }"
          target="_blank"
        >Подробнее
        </vz-button>
      </template>
    </vz-order-view-header-block-multi-offer>
    <vz-light-order-steps v-if="isLightOrder"/>
    <vz-email-modal
      @submit="sendLinkEmail"
    />
  </div>
</template>

<script setup lang="ts">
import {storeToRefs} from 'pinia';
import VzOrderStatus from '~/components/personal/order/vz-order-status.vue';
import {useOrderStore} from '~/stores/order';
import {useLayoutStore} from '~/stores/personal/layout';
import VzEmailModal from '~~/components/modals/vz-email-modal.vue';
import {orderSaveCalc, orderSendCalc} from '~~/api/order';
import {pricePayload} from '~~/common/objects';
import {deepCopyObject} from '~/common/functions';
import {asyncComputed} from '@vueuse/core';
import {getVipText} from '~/api/home';
import VzOrderViewHeaderBlockMultiOffer
  from '~/components/personal/order/vz-order-view/vz-order-view-header-block-multi-offer.vue';
import VzButton from '~/components/ui-kit/vz-button.vue';
import VzLabel from '~/components/ui-kit/vz-label.vue';
import VzLightOrderSteps from '~/components/personal/order/light-order/vz-light-order-steps.vue';
import VzToggler from "~/components/ui-kit/vz-toggler.vue";
import VzIcon from '~/components/ui-kit/vz-icon.vue';

const emit = defineEmits(['update-order'])

const props = defineProps({
  iframe: {
    type: Boolean,
    default: false
  }
})

const {$showToast, $showDialog} = useNuxtApp()
const {isEditOrder} = storeToRefs(useLayoutStore())
const orderStore = useOrderStore()
const route = useRoute()

const isVip = computed(() => {
  return orderStore.orderForm.isVip
})

const isPublic = computed(() => {
  return route.name === 'order-create'
})

const guid = computed(() => {
  return route.params?.guid || ''
})

const isNew = computed(() => {
  return !guid.value
})

const favors = computed(() => {
  return orderStore.orderForm?.meta.favorList || {}
})

const isMultiOrder = computed(() => {
  return orderStore.orderType === 'multi'
})

const isLightOrder = computed(() => {
  return route.name === 'order-light'
})

const orderCloneUrl = computed(() => {
  if (props.iframe) {
    return ''
  }
  if (useRoute().path.startsWith('/order/administer/')) {
    return `/order/create/?clone=${guid.value}`
  }
  return `/personal/order/create/?clone=${guid.value}`
})

const orderTitle = computed(() => {
  if (isNew.value || !orderStore.orderForm.number) {
    if (orderStore.multiOrder.selectedRequestId) {
      return `Заявка ${orderStore.multiOrder.selectedRequestId}`
    }
    return 'Заказ'
  }

  let number = `№${orderStore.orderForm.number}`
  if (orderStore.orderForm.cargo.transportationId) {
    number += ' | ' + orderStore.orderForm.cargo.transportationId
  }
  return `Заказ ${number}`
})

const togglerLabelVariant = computed(() => {
  return isVip.value ? 'info' : 'inactive'
})

const togglerVariant = computed(() => {
  return isVip.value ? 'info' : 'lt-gray'
})

const vipText = asyncComputed(async () => {
  return (await getVipText())['vip-text']
})

function onCopyClick() {
  if (props.iframe) {
    parent.postMessage({
      type: 'order-create',
      other: {clone: guid.value}
    }, '*')
  }
}

async function onChangeVip(value: boolean) {
  orderStore.orderForm.isVip = value
  await orderStore.getOrderPriceAction()
}

function showManageHelp(): void {
  $showDialog({name: 'manager-help'})
}

function onViewOrder(): void {
  isEditOrder.value = false
  orderStore.orderForm = deepCopyObject(orderStore.orderView)
}

async function sendLinkEmail(email: string): Promise<void> {
  const {origin, hash} = await orderSaveCalc(pricePayload(orderStore.orderForm))
  const link = origin + '/order/create/?calculation=' + hash
  await orderSendCalc({link, email, hash})
}
</script>

<style lang="scss" scoped>

</style>
